/**
 * Loads, edits, and saves the `letter_upload` section of the per-facility
 * delivery-steps config. Edits the document title, status source field, the
 * Eligible PA Statuses buckets (alias, include-drug-name, document type +
 * filename), the pre-conditions, and the run conditions; everything else in the
 * section (enabled/action/settings) is preserved via a partial Firestore update.
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FirestoreService } from "../../../../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../../../../api/firebase/references";
import {
  DEFAULT_STALE_AFTER_MINUTES,
  DeliveryStepsDoc,
  LetterUploadConfig,
  TrackStatusSettings,
} from "../../../../../data-model/deliveryStepsConfig";
import { QueryGroup } from "../../../../../data-model/queryBuilder";
import { LocalStorageKeys } from "../../../../../enums/localStorageKeys";
import { getItemFromLocalStorage } from "../../../../../utils/localStorageHelper";
import {
  createPaStatusRow,
  PaStatusRow,
  rowsToPaStatusOptions,
} from "../shared/paStatusRows";
import {
  queryValueToRunWhen,
  runWhenToQueryValue,
} from "../shared/runWhenConversion";
import { StepSettingsFormValues } from "../shared/stepSettingsModal";
import {
  letterUploadRowsFromConfig,
  rowsToDocuments,
} from "./letterUploadUtils";

interface LetterUploadForm {
  enabled: boolean;
  action: string;
  statusField: string;
  settings: TrackStatusSettings;
  documentName: string;
  statusSourceField: string;
  paStatusRows: PaStatusRow[];
  preconditions: QueryGroup;
  conditions: QueryGroup;
}

const serialize = (form: LetterUploadForm): string => JSON.stringify(form);

function formFromConfig(
  config: LetterUploadConfig | undefined,
): LetterUploadForm {
  const doc = config?.document_config;
  return {
    enabled: config?.enabled ?? false,
    action: config?.action ?? "fetch",
    statusField: config?.status_field ?? "",
    settings: { ...(config?.settings ?? {}) },
    documentName: doc?.title_template ?? "",
    statusSourceField: doc?.status_source_field ?? "",
    paStatusRows: letterUploadRowsFromConfig(doc),
    preconditions: runWhenToQueryValue(config?.preconditions),
    conditions: runWhenToQueryValue(config?.run_when),
  };
}

/**
 * Hook driving the Letter Upload tab.
 *
 * @returns Editable form state, dirty/loading/saving flags, and mutators
 */
export function useLetterUploadConfig() {
  const [originalDoc, setOriginalDoc] = useState<DeliveryStepsDoc | null>(null);
  const [form, setForm] = useState<LetterUploadForm>(() =>
    formFromConfig(undefined),
  );
  const [savedSnapshot, setSavedSnapshot] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orgId =
    getItemFromLocalStorage(LocalStorageKeys.HEALTHCARE_FACILITY_ID) ?? "";

  const load = useCallback(async () => {
    if (!orgId.trim()) {
      setError("No healthcare facility id in local storage.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const path = FirestoreDocumentReference.deliveryStepsConfig(orgId);
      const doc =
        await FirestoreService.getDocumentOrNull<DeliveryStepsDoc>(path);
      setOriginalDoc(doc);
      const initial = formFromConfig(doc?.letter_upload);
      setForm(initial);
      setSavedSnapshot(serialize(initial));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const isDirty = useMemo(
    () => serialize(form) !== savedSnapshot,
    [form, savedSnapshot],
  );

  const patch = useCallback(
    (changes: Partial<LetterUploadForm>) =>
      setForm((f) => ({ ...f, ...changes })),
    [],
  );

  const applySettingsForm = useCallback(
    (values: StepSettingsFormValues) => setForm((f) => ({ ...f, ...values })),
    [],
  );

  const updateRow = useCallback(
    (id: string, changes: Partial<PaStatusRow>) =>
      setForm((f) => ({
        ...f,
        paStatusRows: f.paStatusRows.map((r) =>
          r.id === id ? { ...r, ...changes } : r,
        ),
      })),
    [],
  );

  const addRow = useCallback(
    (data: {
      bucket: string;
      includeDrugName: boolean;
      documentType: string;
      documentName: string;
    }) =>
      setForm((f) => ({
        ...f,
        paStatusRows: [
          ...f.paStatusRows,
          createPaStatusRow({
            bucket: data.bucket,
            includeDrugName: data.includeDrugName,
            documentType: data.documentType,
            documentName: data.documentName,
          }),
        ],
      })),
    [],
  );

  const removeRow = useCallback(
    (id: string) =>
      setForm((f) => ({
        ...f,
        paStatusRows: f.paStatusRows.filter((r) => r.id !== id),
      })),
    [],
  );

  const handleDiscard = useCallback(() => {
    setForm(formFromConfig(originalDoc?.letter_upload));
  }, [originalDoc]);

  const handleSave = useCallback(async () => {
    if (!orgId.trim()) {
      toast.error("No healthcare facility id; cannot save.");
      return;
    }
    if (!originalDoc) {
      toast.error("No delivery-steps document to update.");
      return;
    }
    setSaving(true);
    try {
      const staleMinutes =
        originalDoc.global?.stale_threshold_minutes ??
        DEFAULT_STALE_AFTER_MINUTES;
      // Round-trip through JSON to drop `undefined` values — Firestore rejects them.
      const letterUpload = JSON.parse(
        JSON.stringify({
          ...originalDoc.letter_upload,
          enabled: form.enabled,
          action: form.action,
          status_field: form.statusField,
          settings: form.settings,
          document_config: {
            ...originalDoc.letter_upload?.document_config,
            title_template: form.documentName,
            status_source_field: form.statusSourceField,
            pa_status_title_options: rowsToPaStatusOptions(form.paStatusRows),
            documents: rowsToDocuments(form.paStatusRows),
          },
          preconditions: queryValueToRunWhen(form.preconditions, staleMinutes),
          run_when: queryValueToRunWhen(form.conditions, staleMinutes),
        }),
      ) as LetterUploadConfig;
      await FirestoreService.updateDocument(
        `api_config/${orgId}/delivery-steps`,
        "v1",
        { letter_upload: letterUpload },
      );
      setOriginalDoc((prev) =>
        prev ? { ...prev, letter_upload: letterUpload } : prev,
      );
      setSavedSnapshot(serialize(form));
      toast.success("Letter upload configuration saved.");
    } catch {
      toast.error("Failed to save letter upload configuration.");
    } finally {
      setSaving(false);
    }
  }, [orgId, originalDoc, form]);

  return {
    form,
    loading,
    saving,
    error,
    isDirty,
    setDocumentName: (value: string) => patch({ documentName: value }),
    setStatusSourceField: (value: string) =>
      patch({ statusSourceField: value }),
    applySettingsForm,
    setPreconditions: (value: QueryGroup) => patch({ preconditions: value }),
    setConditions: (value: QueryGroup) => patch({ conditions: value }),
    updateRow,
    addRow,
    removeRow,
    handleDiscard,
    handleSave,
  };
}
